// Shared scheme allowlist for note-authored link URLs — the single choke
// point every consumer runs through: EditableNote's execLink/applyLinkAt
// (blocks a javascript:/data:/etc. URL from ever becoming a stored,
// collaborator-shared execCommand payload) and parseNoteHtml's <a href>
// hydration (blocks the same for hrefs a collaborator's client wrote
// directly into the Y.Doc, i.e. bypassing EditableNote's own check — this is
// the render-level backstop). A scheme-less value (relative link, bare host,
// "example.com/a:b") has nothing before its first "/" to abuse and passes
// through unfiltered; anything with a scheme there must be http(s)/mailto.
const ALLOWED_LINK_SCHEMES = new Set(['http:', 'https:', 'mailto:']);

// Shared scheme-detection internals — the same "nothing before the first
// slash to abuse" rule isAllowedLinkUrl has always used, pulled out so
// normalizeLinkUrl (below) can reuse it instead of re-deriving it. Returns
// the scheme AS WRITTEN (including its trailing ":", original casing kept)
// or null when the value has no scheme at all.
const getUrlScheme = (url) => {
    const beforeFirstSlash = String(url ?? '').trim().split('/')[0];
    const colonIndex = beforeFirstSlash.indexOf(':');
    if (colonIndex === -1) return null;
    return beforeFirstSlash.slice(0, colonIndex + 1);
};

export const isAllowedLinkUrl = (url) => {
    const scheme = getUrlScheme(url);
    if (!scheme) return true;
    return ALLOWED_LINK_SCHEMES.has(scheme.toLowerCase());
};

// Simple, deliberately narrow email heuristic for the no-scheme case below:
// exactly one "@", non-empty text on both sides, a "." somewhere in the
// domain part, and nothing that looks like it escaped a plain
// "local@domain.tld" shape (no "/", no whitespace anywhere in the value).
const looksLikeEmail = (value) => {
    if (!value || /\s/.test(value) || value.includes('/')) return false;
    const atIndex = value.indexOf('@');
    if (atIndex === -1 || atIndex !== value.lastIndexOf('@')) return false;
    const local = value.slice(0, atIndex);
    const domain = value.slice(atIndex + 1);
    return Boolean(local) && Boolean(domain) && domain.includes('.');
};

// normalizeLinkUrl's OWN, slightly stricter scheme classification — used
// only below, deliberately NOT folded into getUrlScheme/isAllowedLinkUrl
// (those stay exactly as conservative as they've always been; see
// isAllowedLinkUrl's own doc comment — it's a security backstop, not a UX
// classifier, and has other callers of its own that don't need this).
// getUrlScheme treats ANY "letters-before-a-colon-before-the-first-slash"
// token as a scheme, which also matches things nobody typed a scheme into —
// "localhost:3000" or "example.com:8080/path", a bare host:port. Two extra
// checks before trusting a colon prefix IS a scheme: the token has to look
// like one per RFC 3986's own scheme grammar (a letter, then letters/
// digits/"+"/"-"/"."), AND the very next character after the colon must not
// be a digit (a port number, not a scheme's own delimiter). Either check
// failing means "this colon wasn't introducing a scheme" — falls through to
// the no-scheme path below instead of becoming an unsupported_protocol
// error.
const SCHEME_TOKEN_RE = /^[a-z][a-z0-9+.-]*$/i;
const isRealScheme = (schemeWithColon, trimmed) => {
    const name = schemeWithColon.slice(0, -1);
    if (!SCHEME_TOKEN_RE.test(name)) return false;
    const nextChar = trimmed[schemeWithColon.length];
    return !(nextChar && /[0-9]/.test(nextChar));
};

// Pure normalizer for whatever a user types into the note-link input, run
// BEFORE that value ever reaches editor.resolveLinkTarget (NoteLinkMenu's
// Apply button and Enter key both funnel through this — see ContextMenu.jsx).
// isAllowedLinkUrl (above) stays the final write-time gate regardless — this
// is a courtesy that (a) fills in a scheme for the common "typed a bare
// domain/email, no scheme" case instead of storing something that would fail
// that gate silently, and (b) turns an explicit-but-unsupported scheme into a
// caller-facing error the UI can surface, instead of just swallowing it.
// Web (http/https) links get a canonical trailing "/" — mailto never does,
// and an existing trailing "/" is left alone (no "//" doubling).
const withTrailingSlash = (url) => (url.endsWith('/') ? url : `${url}/`);

export const normalizeLinkUrl = (raw) => {
    const trimmed = String(raw ?? '').trim();
    if (!trimmed) return { error: 'empty' };

    const scheme = getUrlScheme(trimmed);
    if (scheme && isRealScheme(scheme, trimmed)) {
        const lower = scheme.toLowerCase();
        if (lower === 'mailto:') return { url: trimmed };
        if (ALLOWED_LINK_SCHEMES.has(lower)) return { url: withTrailingSlash(trimmed) };
        return { error: 'unsupported_protocol', protocol: lower };
    }

    if (looksLikeEmail(trimmed)) return { url: `mailto:${trimmed}` };
    return { url: withTrailingSlash(`https://${trimmed}`) };
};

// Resolves a stored href for an anchor's `href` ATTRIBUTE at render/click
// time only — never for what's displayed/copied, callers keep showing the
// literal stored value (see NoteLinkPreview.jsx's `truncateLink(href)`/
// `title={href}`). Exists because isAllowedLinkUrl (above) deliberately lets
// a scheme-less value PASS (by design — nothing before the first "/" to
// abuse), so a link stored before normalizeLinkUrl existed, or written by
// any path that bypasses it (a pasted `<a href="example.com">`, a
// collaborator on an older client), reaches the anchor as a bare
// "example.com". A browser has no way to know that's a hostname rather than
// a path, and resolves it RELATIVE TO THE APP'S OWN ORIGIN — clicking it
// opens "<app-origin>/example.com" instead of "https://example.com".
//
// Reuses normalizeLinkUrl's own scheme-detection/email-heuristic/https-
// prefix logic rather than re-deriving it: same rule a user typing that same
// bare value into the link input would get today. Every caller only ever
// invokes this once isAllowedLinkUrl(href) has already returned true, so the
// only two shapes reaching here are "already has an allowed scheme"
// (returned as-is, untouched — an existing working link's href is never
// rewritten) or "no scheme at all" (normalizeLinkUrl always resolves that to
// a `{ url }`, never an `{ error }`) — the `href` fallback below is
// therefore defensive, not a reachable branch.
export const resolveHrefForNavigation = (href) => {
    if (getUrlScheme(href)) return href;
    const result = normalizeLinkUrl(href);
    return result.url ?? href;
};
